import { ChangeDetectionStrategy, Component, computed, effect, OnInit, signal, untracked, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { FlatTreeNode, TreeNode, TreeViewHelper } from '@iot-platform/iot-platform-utils';
import { TranslatePipe } from '@ngx-translate/core';
import { get } from 'lodash';
import { noop } from 'rxjs';
import { FormControlType, FormField, FormGroupComponent } from '../../../../forms';
import { FilterEngineFormControlNames } from '../abstract-filter-form.component';
import { DropdownOption, FilterDropdownFieldComponent } from '../filter-dropdown-field/filter-dropdown-field.component';

@Component({
  selector: 'iot-platform-ui-filter-tree-field',
  standalone: true,
  imports: [FormGroupComponent, MatTreeModule, MatCheckboxModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  templateUrl: './filter-tree-field.component.html',
  styleUrl: './filter-tree-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterTreeFieldComponent extends FilterDropdownFieldComponent implements OnInit {
  readonly filteredItems: WritableSignal<TreeNode[]> = signal([]);
  readonly originalFilteredItems: WritableSignal<DropdownOption[]> = signal([]);
  readonly maxFilters = this.store.maxFilters;
  readonly showTree = computed(() => {
    const filteredItems = this.filteredItems();
    return filteredItems.length > 0;
  });
  isMaxFiltersReached = computed(() => this.store.appliedFilters().length >= this.maxFilters());
  isMultiSelect = computed(() => true); // should be true for tree field
  selectedValues: WritableSignal<DropdownOption[]> = signal([]);

  readonly selectedOptions = computed(() => {
    const selectedOperator = this.selectedOperator();
    const currentFilters = this.currentFilters();
    const items = this.items();
    if (!currentFilters.length || !items.length) {
      return [];
    }
    const existingValueSet = new Set(currentFilters.filter((filter) => filter.criteriaOperator === selectedOperator?.value).map((filter) => filter.value));
    return items.filter((option) => existingValueSet.has(this.formatValue(option)));
  });
  readonly selectOnlyLeafNodes = computed(() => {
    const fieldOptions = this.fieldOptions();
    return fieldOptions?.treeSelectOnlyLeafNodes ?? false;
  });
  readonly selectDistinctNodes = computed(() => {
    const fieldOptions = this.fieldOptions();
    return fieldOptions?.treeSelectDistinctNodes ?? false;
  });
  // Tree helper instance
  private treeHelper!: TreeViewHelper<DropdownOption>;
  private readonly treeConfig = computed(() => {
    const fieldOptions = this.fieldOptions();
    return {
      idProperty: get(fieldOptions, 'treeIdProperty', 'id'),
      parentProperty: get(fieldOptions, 'treeParentProperty', 'parentId'),
      levelProperty: get(fieldOptions, 'treeLevelProperty', 'level'),
      includeAncestors: !!fieldOptions?.treeIncludeAncestors,
      selectOnlyLeafNodes: !!fieldOptions?.treeSelectOnlyLeafNodes,
      selectDistinctNodes: !!fieldOptions?.treeSelectDistinctNodes,
      suppressFirstNodeExpand: !!fieldOptions?.treeSuppressFirstNodeExpand
    };
  });

  constructor() {
    super();

    // Initialize tree helper with default config
    this.treeHelper = new TreeViewHelper<DropdownOption>({
      idProperty: 'id',
      parentProperty: 'parentId'
    });

    effect(() => {
      this.selectedOperator();
      untracked(() => {
        this.searchTermControl?.reset();
        const treeData = this.treeHelper.buildTreeStructure(this.items());
        this.onFilteredItems(treeData);
      });
    });

    // Update tree helper config when it changes
    effect(() => {
      const config = this.treeConfig();
      this.treeHelper = new TreeViewHelper<DropdownOption>(config);
      untracked(() => {
        const treeData = this.treeHelper.buildTreeStructure(this.items());
        this.onFilteredItems(treeData);
      });
    });
  }

  // Expose tree helper properties for template
  get treeControl() {
    return this.treeHelper.treeControl;
  }

  get checklistSelection() {
    return this.treeHelper.checklistSelection;
  }

  get dataSource() {
    return this.treeHelper.dataSource;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.form.addControl('filteredItems', new FormControl<any>([]));
  }

  override initializeControl(): void {
    const selectedOptions = this.selectedOptions();
    this.selectedValues.set(selectedOptions);
    untracked(() => {
      this.expandFirstMatchedItemAndSelectNodesFromValues();
    });
  }

  expandFirstMatchedItemAndSelectNodesFromValues(): void {
    queueMicrotask(() => {
      this.treeHelper.expandFirstMatchedItem(this.originalFilteredItems(), (item: DropdownOption) => this.formatValue(item));
      this.treeHelper.selectNodesFromValues(this.selectedValues(), (item: DropdownOption) => this.formatValue(item));
      this.cdr.detectChanges();
    });
  }

  override getControlValue(): never {
    return this.selectedValues() as never;
  }

  override hasValidValue(): boolean {
    return true;
  }

  override getSearchFields(): Partial<FormField>[] {
    return [
      {
        type: FormControlType.AUTO_COMPLETE,
        name: signal(FilterEngineFormControlNames.SEARCH_TERM),
        label: this.searchTermField.label,
        items: this.items,
        fxFlex: signal('100%'),
        autocomplete: signal(false),
        showSpinner: computed(() => this.isLoading()),
        visible: this.requiresValue,
        onReady: (params: any) => {
          this.elementRef = params.ref;
        },
        trackBy: (item: DropdownOption) => this.formatValue(item),
        displayBy: (item: DropdownOption) => this.formatLabel(item),
        onReset: () => this.onAutoCompleteReset(),
        onFilteredItems: (filteredItems: DropdownOption[]) => {
          this.originalFilteredItems.set(filteredItems);
          const allItems = this.items();
          this.treeHelper.setCurrentSelectionList(allItems, this.selectedValues(), (item: DropdownOption) => this.formatValue(item));
          const treeData = this.treeHelper.buildTreeFromFilteredItems(filteredItems, allItems);
          this.onFilteredItems(treeData);
          this.expandFirstMatchedItemAndSelectNodesFromValues();
        }
      }
    ];
  }

  onFilteredItems(filteredItems: TreeNode<DropdownOption>[]): void {
    this.filteredItems.set(filteredItems);
    this.treeHelper.updateDataSource(filteredItems);
  }

  override clearValue(): void {
    noop();
  }

  override handleReset(): void {
    this.treeHelper.clearSelection();
    this.searchTermControl?.reset();
    super.handleReset();
    this.onAutoCompleteReset();
    this.selectedValues.set([]);
  }

  override onDataLoaded(data: DropdownOption[]): void {
    this.treeHelper.clearSelection();
    super.onDataLoaded(data);
    const treeData = this.treeHelper.buildTreeStructure(data);
    this.onFilteredItems(treeData);
  }

  readonly hasChild = (_: number, node: FlatTreeNode<DropdownOption>): boolean => this.treeHelper.hasChild(_, node);

  getNodeLabel(node: FlatTreeNode<DropdownOption>): string {
    return this.formatLabel(node) as string;
  }

  override isOptionDisabled(node: FlatTreeNode<DropdownOption>): boolean {
    const disabled = this.disabled();
    if (this.selectOnlyLeafNodes() && node.expandable) {
      const someSelected = this.treeHelper.descendantsPartiallySelected(node);
      const allSelected = this.treeHelper.descendantsAllSelected(node);
      return someSelected || allSelected ? false : disabled;
    }
    const values = this.selectedValues();
    const isItemSelected = values?.some((e: DropdownOption) => this.formatValue(e) === this.formatValue(node));
    return !isItemSelected && disabled;
  }

  isNodeSelected(node: FlatTreeNode<DropdownOption>): boolean {
    return this.treeHelper.isNodeSelected(node);
  }

  getDescendantsCount(node: FlatTreeNode<DropdownOption>): number {
    return this.treeHelper.getDescendantsCount(node);
  }

  descendantsAllSelected(node: FlatTreeNode<DropdownOption>): boolean {
    return this.treeHelper.descendantsAllSelected(node);
  }

  descendantsPartiallySelected(node: FlatTreeNode<DropdownOption>): boolean {
    return this.treeHelper.descendantsPartiallySelected(node);
  }

  itemSelectionToggle(node: FlatTreeNode<DropdownOption>): void {
    this.treeHelper.toggleNodeSelection(
      node,
      (item: DropdownOption) => this.formatValue(item),
      () => this.updateSelectedValues()
    );
  }

  leafItemSelectionToggle(node: FlatTreeNode<DropdownOption>): void {
    this.treeHelper.toggleLeafSelection(
      node,
      (item: DropdownOption) => this.formatValue(item),
      () => this.updateSelectedValues()
    );
  }

  private updateSelectedValues(): void {
    const selected = this.treeHelper.getSelectedNodes();
    const items = this.items();

    const selectedSet = new Set(selected.map((node: FlatTreeNode<DropdownOption>) => this.formatValue(node as any)));
    const selectedItems = items.filter((item) => selectedSet.has(this.formatValue(item)));

    this.selectedValues.set(selectedItems);

    if (this.isAutoApply()) {
      this.handleApply();
    }
  }

  private onAutoCompleteReset(): void {
    this.originalFilteredItems.set([]);
    const treeData = this.treeHelper.buildTreeStructure(this.items());
    this.onFilteredItems(treeData);
    this.expandFirstMatchedItemAndSelectNodesFromValues();
  }
}
